const NAVIGATION_KEY_NAMES = ['ArrowDown', 'ArrowUp'];

export type configObject = {
    listItemClassNames: string;
    isRoving?: boolean;
    listGroupElement?: HTMLElement;
};

type configParams = configObject & { targetElement: HTMLElement };

/**
 * A construct that manages keyboard navigation as it relates to lists.
 * @class
 */

class ListKeyboardAccess {
    private listItemClassNames: Array<string>;
    private listParentElement: HTMLElement;
    private boundFocusInHandler: EventListener;
    private boundKeyDownHandler: EventListener;
    private listGroupElement: HTMLElement | undefined;
    // a current index based on an ancestor parent i.e. `listGroupElement`.
    private currentRootIndex: number = -1;
    // a current index based on an immediate list parent i.e. `listParentElement`.
    private currentIndex: number = -1;
    private isRoving: boolean = false;

    static isWindowEventBound: boolean = false;

    constructor(options: configParams) {
        const { listGroupElement, targetElement } = options;
        this.listParentElement = targetElement;
        this.listGroupElement = listGroupElement;
        this.isRoving = (options.isRoving ?? false) && !!this.listGroupElement;

        // converting a string list into an array of CSS class names (note: not selectors).
        this.listItemClassNames = options.listItemClassNames
            ?.split(',')
            .map((className) => className.trim());
        // Attempting to only bind this event once for the purpose of list navigation.
        if (!ListKeyboardAccess.isWindowEventBound) {
            window.addEventListener(
                'keydown',
                ListKeyboardAccess.windowKeyUpHandler,
            );
            ListKeyboardAccess.isWindowEventBound = true;
        }

        if (this.listItemClassNames?.join('').length) {
            this.boundFocusInHandler = this.focusInHandler.bind(this);
            this.boundKeyDownHandler = this.keyDownHandler.bind(this);

            this.listParentElement.addEventListener(
                'focusin',
                this.boundFocusInHandler,
                {
                    capture: true,
                },
            );
            this.listParentElement.addEventListener(
                'keydown',
                this.boundKeyDownHandler,
            );
        } else {
            throw Error('ListKeyboardAccess requires listItemClassNames');
        }
    }

    destroy() {
        if (ListKeyboardAccess.isWindowEventBound) {
            window.removeEventListener(
                'keydown',
                ListKeyboardAccess.windowKeyUpHandler,
            );
            ListKeyboardAccess.isWindowEventBound = false;
        }

        this.listParentElement?.removeEventListener(
            'focusin',
            this.boundFocusInHandler,
            {
                capture: true,
            },
        );

        this.listParentElement?.removeEventListener(
            'keydown',
            this.boundKeyDownHandler,
        );
    }

    private getListItems(
        fromlistGroupElement: boolean = false,
    ): Array<HTMLElement> {
        const { listGroupElement, listParentElement } = this;
        const root =
            fromlistGroupElement && listGroupElement
                ? listGroupElement
                : listParentElement;
        const selectors = getSelectorsFromCSSClassNames(
            this.listItemClassNames.join(','),
        );
        return Array.from(root.querySelectorAll(selectors));
    }

    private focusInHandler(event: any) {
        const currentListItem = this.findListItem(event.target, []);
        const listItems = this.getListItems();
        // bail if no list items or currentListItem
        if (!listItems.length || !currentListItem) return;
        this.currentIndex = listItems.indexOf(currentListItem);

        this.currentRootIndex = this.getListItems(this.isRoving)?.indexOf(
            currentListItem,
        );

        if (this.currentIndex >= 0 && this.isRoving) {
            for (let i = 0; i < listItems.length; i++) {
                setTabFocusable(listItems[i], i === this.currentIndex);
            }
        }
    }

    private keyDownHandler(event: any) {
        if (
            !NAVIGATION_KEY_NAMES.includes(event.key) ||
            this.currentIndex < 0
        ) {
            return;
        }
        const currentIndex = this.isRoving
            ? this.currentRootIndex
            : this.currentIndex;
        const listItems = this.getListItems(this.isRoving);

        let nextIndex =
            event.key === 'ArrowUp'
                ? Math.max(0, currentIndex - 1)
                : Math.min(currentIndex + 1, listItems.length - 1);

        focusVisibleItemByIndex(nextIndex, currentIndex, listItems);
    }

    /**
     * A helper method to find the closest focusable list item.
     * @param sourceElement origin of traversal
     * @returns  HTMLElement | null
     */
    private findListItem(
        sourceElement: HTMLElement | null,
        chekcedNodes: Array<HTMLElement | null>,
    ): HTMLElement | null {
        if (!sourceElement || chekcedNodes.includes(sourceElement)) {
            return null;
        }
        chekcedNodes.push(sourceElement);
        if (hasSomeClassNames(this.listItemClassNames, sourceElement)) {
            return sourceElement;
        }
        const childNodes = sourceElement?.parentElement?.childNodes ?? [];
        for (let i = 0; i < childNodes.length; i++) {
            return this.findListItem(
                childNodes[i] as HTMLElement,
                chekcedNodes,
            );
        }
        return (
            this.findListItem(sourceElement.parentElement, chekcedNodes) ||
            this.findListItem(
                sourceElement.previousSibling as HTMLElement,
                chekcedNodes,
            ) ||
            this.findListItem(
                sourceElement.nextElementSibling as HTMLElement,
                chekcedNodes,
            )
        );
    }

    /**
     * Event handler for the window to stop scrolling the page when users use the arrow keys.
     * @param event
     */
    static windowKeyUpHandler(event: any) {
        if (NAVIGATION_KEY_NAMES.includes(event.key)) {
            event.preventDefault();
        }
    }
}

function hasSomeClassNames(
    classNames: Array<string>,
    element: HTMLElement,
): boolean {
    if (element === null) return false;
    return [...classNames].some((className) =>
        element.classList.contains(className),
    );
}

function focusVisibleItemByIndex(
    index: number,
    targetIndex: number,
    listItems: Array<HTMLElement>,
) {
    const direction = index - targetIndex > 0 ? 1 : -1;
    const listItem = listItems[index];
    if (!listItem) {
        return;
    }
    // Sometimes the list item itself is visible, but the parent
    // is not--like the search button in the nav bar.
    // Check visibility for the element and its parent before assigning focus.
    if (isItemVisible(listItem) && isItemVisible(listItem.parentElement)) {
        listItems[index].focus();
    } else {
        focusVisibleItemByIndex(index + direction, targetIndex, listItems);
    }
}

function isItemVisible(element: HTMLElement | null): boolean {
    if (element === null) return false;
    const { display, visibility, opacity } = window.getComputedStyle(element);
    return display !== 'none' && visibility !== 'hidden' && opacity !== '0';
}

function getSelectorsFromCSSClassNames(classes: string): string {
    if (!classes) return '';
    return classes
        .split(',')
        .map((name) => `.${name.trim()}`)
        .join(',');
}

/**
 * sets tabindex for an element following W3C Web standards.
 * @param element HTMLElement
 * @param isTabFocusable boolean
 */
export function setTabFocusable(element: HTMLElement, isTabFocusable: boolean) {
    const interactableNodeNames = ['A', 'BUTTON'];
    if (isTabFocusable) {
        if (interactableNodeNames.includes(element.nodeName)) {
            element.removeAttribute('tabindex');
        } else {
            element.setAttribute('tabindex', '0');
        }
    } else {
        element.setAttribute('tabIndex', '-1');
    }
}

/**
 * set up mutation observer to ensure tab-focusablility is set appropriately based on the list item's focusability.
 * @param listItemNode
 * @param interactableTargets
 * @returns
 */
export function initListItemObserver(
    listItemNode: HTMLElement,
    interactableTargets: Array<HTMLElement>,
): MutationObserver {
    const observer = new MutationObserver((mutationsList) => {
        let tabindex: number;
        for (let mutation of mutationsList) {
            if (mutation.type === 'attributes' && interactableTargets.length) {
                for (let i = 0; i < interactableTargets.length; i++) {
                    tabindex = Number(
                        (mutation.target as HTMLElement).getAttribute(
                            'tabindex',
                        ),
                    );
                    setTabFocusable(interactableTargets[i], tabindex >= 0);
                }
            }
        }
    });
    if (listItemNode) {
        observer.observe(listItemNode, { attributes: true });
    }
    return observer;
}

export function listKeyboardAccess(
    targetElement: HTMLElement,
    options: configObject = { listItemClassNames: '' },
) {
    const listKeyboardAXInstance = new ListKeyboardAccess({
        targetElement,
        ...options,
    });
    return {
        destroy() {
            listKeyboardAXInstance.destroy();
        },
    };
}

export default listKeyboardAccess;
